<template>
  <div id="dashboard-analytics">
    <h3 class="mb-5">Gestão de Pedidos</h3>
    <span>Aqui você pode extrair uma tabela ou planilha consolidada de orçamentos, carrinhos e pedidos das lojas selecionadas.</span>

    <vs-alert active="true" class="mb-8 mt-8" color="warning" title="Aviso">
      Preencha os campos abaixo para extrair um relatório
    </vs-alert>

    <div class="vx-row">
      <div class="vx-col w-full md:w-1/4 lg:w-1/4 xl:w-1/4 mb-base">
        <div class="w-12/12">
          <div class="w-full justify-center items-center">
            <span class="phone-number-span mr-12">Período (de)</span>
          </div>
          <div class="w-full">
            <flat-pickr
              v-model="startAt"
              :config="configDateTimePicker"
              name="Período (de)"
              placeholder="Escolha um dia"
            />
          </div>
        </div>
      </div>

      <div class="vx-col w-full md:w-1/4 lg:w-1/4 xl:w-1/4 mb-base">
        <div class="w-12/12">
          <div class="w-full justify-center items-center">
            <span class="phone-number-span mr-12">Período (até)</span>
          </div>
          <div class="w-full">
            <flat-pickr
              v-model="finishAt"
              name="Período (até)"
              placeholder="Escolha um dia"
              :disabled="startAt === ''"
              :config="finishAtConfig(configDateTimePicker)"
            />
          </div>
        </div>
      </div>

      <div class="vx-col w-full md:w-1/4 lg:w-1/4 xl:w-1/4 mb-base">
        <div class="w-12/12">
          <div class="w-full justify-center items-center">
            <span class="phone-number-span">Status</span>
          </div>
          <v-select
            v-model="statusFilter"
            :dir="$vs.rtl ? 'rtl' : 'ltr'"
            :options="statusOptions"
            :reduce="option => option.id"
            label="label"
            class="w-full"
            name="Status"
          />
        </div>
      </div>
    </div>

    <div class="w-full mb-base">
      <div class="p-2 rounded-lg shadow">
        <tenants-slim-list
          checkbox
          :stripe="false"
          :selecteds="selectedTenantIds"
          :only-actives="true"
          @selectedTenant="onTenantsChanged"
        />
      </div>
    </div>

    <div class="vx-col w-full">
      <div class="w-12/12">
        <div class="w-full">
          <vs-button
            class="mt-5 mr-4"
            color="rgb(0, 205, 216)"
            :disabled="!canSearch"
            @click="search(1)"
          >
            Gerar Relatório
          </vs-button>
          <vs-button
            class="mt-5 mr-4"
            type="border"
            color="primary"
            :disabled="!canSearch"
            @click="exportCsv"
          >
            Baixar CSV
          </vs-button>
          <vs-button class="mt-5" :disabled="!canSearch" @click="exportXlsx">
            Baixar Relatório
          </vs-button>
        </div>
      </div>
    </div>

    <div id="table-report">
      <div v-if="searched && totalItems > 0">
        <vs-dropdown class="cursor-pointer mb-4 mr-4 mt-4" vs-trigger-click>
          <div class="p-4 border border-solid d-theme-border-grey-light rounded-full d-theme-dark-bg cursor-pointer flex items-center justify-between font-medium">
            <span class="mr-2">
              {{ pageStart }} - {{ pageEnd }} de {{ totalItems }}
            </span>
            <feather-icon icon="ChevronDownIcon" svgClasses="h-4 w-4" />
          </div>
          <vs-dropdown-menu>
            <vs-dropdown-item @click="changeItemsPerPage(4)">
              <span>4</span>
            </vs-dropdown-item>
            <vs-dropdown-item @click="changeItemsPerPage(10)">
              <span>10</span>
            </vs-dropdown-item>
            <vs-dropdown-item @click="changeItemsPerPage(15)">
              <span>15</span>
            </vs-dropdown-item>
            <vs-dropdown-item @click="changeItemsPerPage(20)">
              <span>20</span>
            </vs-dropdown-item>
          </vs-dropdown-menu>
        </vs-dropdown>
      </div>

      <vs-table
        v-if="searched"
        ref="table"
        :data="tableData"
        :max-items="itemsPerPage"
        class="table-report-container mt-4"
        noDataText="Sem dados disponíveis"
      >
        <template slot="header">
          <h3 class="mb-3">Pedidos consolidados</h3>
        </template>

        <template slot="thead">
          <vs-th>ID Pedido</vs-th>
          <vs-th>Loja (Tenant)</vs-th>
          <vs-th>Atendente</vs-th>
          <vs-th>Cliente</vs-th>
          <vs-th>Valor Total</vs-th>
          <vs-th>Status</vs-th>
          <vs-th>Data/Hora</vs-th>
        </template>

        <template slot-scope="{ data }">
          <vs-tr v-for="(row, index) in data" :key="index" :data="row">
            <vs-td>
              <a
                :href="orderAdminUrl(row)"
                class="text-primary"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ row.order_id }}
              </a>
            </vs-td>
            <vs-td>
              <a
                :href="tenantAdminUrl(row)"
                class="text-primary"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ row.tenant_name }}
              </a>
              <span class="text-xs text-grey block">ID {{ row.website_id }}</span>
            </vs-td>
            <vs-td>
              <p class="product-name font-medium truncate">{{ row.attendant_name }}</p>
            </vs-td>
            <vs-td>
              <p class="product-name font-medium truncate">{{ row.customer_name }}</p>
            </vs-td>
            <vs-td>{{ formatCurrency(row.total_value) }}</vs-td>
            <vs-td>
              <vs-chip :color="row.status_category === 'quote_or_cart' ? 'warning' : 'success'">
                {{ row.status_name && row.status_name !== '-' ? row.status_name : row.status_label }}
              </vs-chip>
              <span
                v-if="row.status_name && row.status_name !== '-' && row.status_label"
                class="text-xs text-grey block"
              >
                {{ row.status_label }}
              </span>
            </vs-td>
            <vs-td>{{ row.created_at }}</vs-td>
          </vs-tr>
        </template>

        <template slot="empty">
          <div class="empty-state py-6">
            <div class="title font-medium">Nenhum pedido encontrado</div>
            <div class="subtitle text-sm text-grey mt-2">
              Ajuste os filtros ou o período e gere o relatório novamente.
            </div>
          </div>
        </template>
      </vs-table>

      <div class="mt-3 mr-4 flex justify-end" v-if="searched && totalPages > 1">
        <vs-pagination
          :total="totalPages"
          v-model="page"
          max="7"
          @change="handleChangePage"
        />
      </div>
    </div>
  </div>
</template>

<script>
import ExcelJS from 'exceljs'
import vSelect from 'vue-select'
import { mapGetters } from 'vuex'
import 'flatpickr/dist/flatpickr.css'
import flatPickr from 'vue-flatpickr-component'
import { Portuguese } from 'flatpickr/dist/l10n/pt.js'
import TenantsSlimList from '@/views/Tenants/TenantsSlimList.vue'

const EXPORT_COLUMNS = [
  { key: 'order_id', header: 'ID Pedido' },
  { key: 'tenant_name', header: 'Loja (Tenant)' },
  { key: 'website_id', header: 'ID Tenant' },
  { key: 'attendant_name', header: 'Atendente' },
  { key: 'customer_name', header: 'Cliente' },
  { key: 'total_value', header: 'Valor Total' },
  { key: 'status_name', header: 'Status' },
  { key: 'status_label', header: 'Tipo' },
  { key: 'created_at', header: 'Data/Hora' },
  { key: 'integration_id', header: 'ID Integração ERP' },
  { key: 'finished_order_id', header: 'ID Externo VTEX/ERP' },
  { key: 'erp_name', header: 'ERP' },
]

export default {
  name: 'OrdersSalesReport',
  components: { TenantsSlimList, flatPickr, vSelect },
  data() {
    return {
      startAt: '',
      finishAt: '',
      statusFilter: 'all',
      selectedTenantIds: [],
      tableData: [],
      page: 1,
      itemsPerPage: 4,
      totalPages: 0,
      totalItems: 0,
      searched: false,
      statusOptions: [
        { id: 'all', label: 'Todos' },
        { id: 'quote', label: 'Orçamento/Carrinho' },
        { id: 'order', label: 'Pedidos em andamento' },
        { id: 'finalized', label: 'Pedidos finalizados' },
      ],
      configDateTimePicker: {
        enableTime: false,
        noCalendar: false,
        dateFormat: 'd/m/Y',
        locale: Portuguese,
      },
    }
  },
  computed: {
    ...mapGetters(['token', 'tenants']),
    canSearch() {
      return Boolean(this.startAt && this.finishAt && this.selectedWebsiteIds.length)
    },
    selectedWebsiteIds() {
      return this.tenants
        .filter((tenant) => this.selectedTenantIds.includes(tenant.id))
        .map((tenant) => tenant.website_id)
    },
    websiteIdsParam() {
      return this.selectedWebsiteIds.join(',')
    },
    pageStart() {
      if (!this.totalItems) {
        return 0
      }
      return (this.page - 1) * this.itemsPerPage + 1
    },
    pageEnd() {
      return Math.min(this.page * this.itemsPerPage, this.totalItems)
    },
  },
  methods: {
    finishAtConfig(config) {
      return { ...config, minDate: this.startAt }
    },
    datePtToEng(dateString) {
      if (!dateString) return dateString
      const parts = dateString.split('/')
      return `${parts[2]}-${parts[1]}-${parts[0]}`
    },
    onTenantsChanged(selected) {
      this.selectedTenantIds = selected
    },
    orderAdminUrl(row) {
      const base = process.env.VUE_APP_PEDBOT_ADMIN_URL
      const path = row.status_category === 'quote_or_cart'
        ? 'orders/pre-orders'
        : 'orders/product-orders'
      return `${base}/${row.tenant_slug}/${path}?orderId=${row.order_id}`
    },
    tenantAdminUrl(row) {
      return this.orderAdminUrl(row)
    },
    formatCurrency(value) {
      return Number(value || 0).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      })
    },
    notifyError(text) {
      this.$vs.notify({ color: 'danger', title: 'Erro', text })
    },
    verify() {
      const permissions = localStorage.getItem('permissions')
      const twoFactor = localStorage.getItem('twoFactor')
      if (!permissions && !twoFactor) {
        this.$router.push('/user-settings')
      } else if (!permissions && twoFactor) {
        this.$router.push('/two-factor')
      }
    },
    async handleChangePage(newPage) {
      this.page = newPage
      await this.search(newPage)
    },
    async changeItemsPerPage(limit) {
      if (this.itemsPerPage === limit) {
        return
      }
      this.itemsPerPage = limit
      await this.search(1)
    },
    async search(page = 1) {
      if (!this.canSearch) {
        return this.notifyError('Selecione período e ao menos uma loja.')
      }

      this.$vs.loading()
      this.page = page
      this.searched = true

      try {
        const response = await this.$store.dispatch('getConsolidatedOrders', {
          token: this.token,
          startAt: this.datePtToEng(this.startAt),
          finishAt: this.datePtToEng(this.finishAt),
          websiteIds: this.websiteIdsParam,
          statusFilter: this.statusFilter,
          page: this.page,
          limit: this.itemsPerPage,
          export: false,
        })

        const data = response.data || {}
        this.tableData = data.items || []
        this.totalItems = data.total_items || 0
        this.totalPages = data.total_pages || 0

        if (this.totalPages > 0 && this.page > this.totalPages) {
          await this.search(this.totalPages)
        }
      } catch (error) {
        this.notifyError('Não foi possível carregar os pedidos.')
        this.tableData = []
        this.totalItems = 0
        this.totalPages = 0
      } finally {
        this.$vs.loading.close()
      }
    },
    async fetchExportRows() {
      const response = await this.$store.dispatch('getConsolidatedOrders', {
        token: this.token,
        startAt: this.datePtToEng(this.startAt),
        finishAt: this.datePtToEng(this.finishAt),
        websiteIds: this.websiteIdsParam,
        statusFilter: this.statusFilter,
        export: true,
      })

      return (response.data && response.data.items) ? response.data.items : []
    },
    rowToExportValues(row) {
      return EXPORT_COLUMNS.map((column) => {
        if (column.key === 'total_value') {
          return this.formatCurrency(row.total_value)
        }
        return row[column.key] != null ? row[column.key] : ''
      })
    },
    async exportCsv() {
      this.$vs.loading()
      try {
        const rows = await this.fetchExportRows()
        const headers = EXPORT_COLUMNS.map((column) => column.header)
        const lines = [
          headers.join(';'),
          ...rows.map((row) => this.rowToExportValues(row).map((v) => `"${String(v).replace(/"/g, '""')}"`).join(';')),
        ]
        const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = 'gestao-pedidos.csv'
        link.click()
        window.URL.revokeObjectURL(link.href)
      } catch (error) {
        this.notifyError('Erro ao exportar CSV.')
      } finally {
        this.$vs.loading.close()
      }
    },
    async exportXlsx() {
      this.$vs.loading()
      try {
        const rows = await this.fetchExportRows()
        const workbook = new ExcelJS.Workbook()
        const worksheet = workbook.addWorksheet('Gestão de Pedidos')
        worksheet.addRow(EXPORT_COLUMNS.map((column) => column.header))
        rows.forEach((row) => worksheet.addRow(this.rowToExportValues(row)))
        const buffer = await workbook.xlsx.writeBuffer()
        const blob = new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = 'gestao-pedidos.xlsx'
        link.click()
        window.URL.revokeObjectURL(link.href)
      } catch (error) {
        this.notifyError('Erro ao exportar relatório.')
      } finally {
        this.$vs.loading.close()
      }
    },
  },
  created() {
    if (!this.tenants.length) {
      this.$store.dispatch('getAllTenants', { token: this.token })
    }
  },
  mounted() {
    this.verify()
  },
}
</script>

<style lang="scss">
#table-report {
  width: 100%;
  overflow: auto;

  .empty-state {
    text-align: center;
  }

  .vs-con-table {
    .product-name {
      max-width: 23rem;
    }

    .vs-table--header {
      display: flex;
      flex-wrap: wrap-reverse;
      margin-left: 1.5rem;
      margin-right: 1.5rem;

      > span {
        display: flex;
        flex-grow: 1;
      }

      .vs-table--search {
        padding-top: 0;

        .vs-table--search-input {
          padding: 0.9rem 2.5rem;
          font-size: 1rem;

          & + i {
            left: 1rem;
          }

          &:focus + i {
            left: 1rem;
          }
        }
      }
    }

    .vs-table {
      border-collapse: separate;
      border-spacing: 0 1.3rem;
      padding: 0 1rem;

      tr {
        box-shadow: 0 4px 20px 0 rgba(0, 0, 0, .05);

        td {
          padding: 10px;

          &:first-child {
            border-top-left-radius: .5rem;
            border-bottom-left-radius: .5rem;
          }

          &:last-child {
            border-top-right-radius: .5rem;
            border-bottom-right-radius: .5rem;
          }
        }

        td.td-check {
          padding: 20px !important;
        }
      }
    }

    .vs-table--thead {
      th {
        padding-top: 0;
        padding-bottom: 0;

        .vs-table-text {
          text-transform: uppercase;
          font-weight: 600;
          width: 100px;
        }
      }

      th.td-check {
        padding: 0 15px !important;
      }

      tr {
        background: none;
        box-shadow: none;
      }
    }

    .vs-table--pagination {
      justify-content: center;
    }
  }
}

.v-select {
  background: #fff;
}

#dashboard-analytics {
  .phone-number-span {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
  }
}
</style>
